import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Field, Notice } from '../patterns';
import type { Tenant } from '../../services/tenantService';

interface Props {
  show: boolean;
  tenant: Tenant | null;
  onHide: () => void;
  onSave: (input: { tenantId: string; name: string; farmTimezone: string }) => Promise<void>;
}

const TenantEditModal: React.FC<Props> = ({ show, tenant, onHide, onSave }) => {
  const { t } = useTranslation();
  const isEdit = tenant !== null;

  const [tenantId, setTenantId] = useState('');
  const [name, setName] = useState('');
  const [farmTimezone, setFarmTimezone] = useState('Africa/Johannesburg');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (tenant) {
      setTenantId(tenant.tenantId);
      setName(tenant.name);
      setFarmTimezone(tenant.farmTimezone || 'Africa/Johannesburg');
    } else {
      setTenantId('');
      setName('');
      setFarmTimezone('Africa/Johannesburg');
    }
    setError(null);
  }, [tenant, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await onSave({ tenantId: tenantId.trim(), name: name.trim(), farmTimezone });
      onHide();
    } catch (err: any) {
      setError(err?.response?.data?.error || t('system.tenants.errors.saveFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      title={isEdit ? t('system.tenants.modal.editTitle') : t('system.tenants.modal.createTitle')}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Field
            label={t('system.tenants.modal.fields.tenantId')}
            controlId="tenantId"
            required
            helpText={!isEdit ? t('system.tenants.modal.tenantIdHelp') : undefined}
          >
            <input
              id="tenantId"
              className="pat-input"
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              disabled={isEdit}
              required
            />
          </Field>
          <Field label={t('system.tenants.modal.fields.name')} controlId="name" required>
            <input
              id="name"
              className="pat-input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </Field>
          <Field label={t('system.tenants.modal.fields.farmTimezone')} controlId="farmTimezone" required>
            <input
              id="farmTimezone"
              className="pat-input"
              value={farmTimezone}
              onChange={e => setFarmTimezone(e.target.value)}
              required
            />
          </Field>
          {error && (
            <Notice tone="danger">
              {error}
            </Notice>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="pat-btn pat-btn--subtle"
            onClick={onHide}
            disabled={submitting}
          >
            {t('system.tenants.modal.cancel')}
          </button>
          <button
            type="submit"
            className="pat-btn pat-btn--primary"
            disabled={submitting}
          >
            {t('system.tenants.modal.save')}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default TenantEditModal;
